// The call owner's own camera - the "My camera" layer. This is the owner's camera specifically, not
// whichever browser happens to be viewing the page (on the guest page those are different people) - see
// callContext.onOwnerStream at each page's createSharedServices() call for how each page tells them apart.
// Falls back to a direct getUserMedia so the layer still shows a real live preview when edited standalone,
// before any call session exists.
import { createParticipantOverlay } from '../participantOverlay';
import { watchPicture } from '../../streamPicture';

export default async function mountCamera(host) {
	const video = document.createElement('video');
	video.autoplay = true;
	video.muted = true;
	video.playsInline = true;
	// fill, not cover: this layer's box is already the crop/stretch the editor's own layer tools computed
	// (same as an image/video layer's own content) - covering again here would crop it a second time.
	Object.assign(video.style, { width: '100%', height: '100%', objectFit: 'fill', display: 'none' });
	const placeholder = document.createElement('div');
	Object.assign(placeholder.style, {
		width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
		background: 'rgba(255,255,255,.08)', color: '#fff', fontSize: '.8rem', textAlign: 'center', padding: '.5rem',
	});
	host.container.appendChild(video);
	host.container.appendChild(placeholder);
	const overlay = createParticipantOverlay(host.container);

	let ownedStream = null; // only set (and later stopped) when this mounter opened its own fallback camera
	let unsubscribeOwn = null;
	let stopWatching = null;
	let stopped = false;

	function showStream(stream) {
		video.srcObject = stream;
		video.style.display = 'block';
		placeholder.style.display = 'none';
		overlay.setCameraOff(false);
		overlay.setStream(stream);
	}
	// A real stream with no picture (mic granted, camera left off) - still worth listening to for the speaking ring.
	function showCameraOff(stream) {
		// Stays displayed (the placeholder covers it): frame watching needs the video to keep playing.
		video.style.display = stream ? 'block' : 'none';
		placeholder.style.display = 'none';
		overlay.setCameraOff(true);
		overlay.setStream(stream);
	}
	function showMessage(text) {
		placeholder.textContent = text;
		placeholder.style.display = 'flex';
		video.style.display = 'none';
		overlay.setCameraOff(false);
		overlay.setStream(null);
	}

	// The owner's name: the studio editor knows it directly; any other page finds the owner in the caller list.
	let unsubscribeCallers = null;
	if(host.callContext?.onCallers) {
		unsubscribeCallers = host.callContext.onCallers(callers => {
			const owner = callers.find(c => c.isOwner);
			overlay.setName(host.callContext.ownName?.() || owner?.name || '');
			overlay.setMuted(host.callContext.ownMicMuted?.() ?? !!owner?.micMuted);
		});
	}

	if(host.callContext?.onOwnerStream) {
		unsubscribeOwn = host.callContext.onOwnerStream(stream => {
			// Also fires when the studio's own mic is toggled - see callControlsMixin.toggleMic.
			if(host.callContext.ownMicMuted) overlay.setMuted(host.callContext.ownMicMuted());
			stopWatching?.();
			video.srcObject = stream;
			stopWatching = watchPicture(stream, has => has ? showStream(stream) : showCameraOff(stream), { video });
		});
	} else {
		showMessage('Connecting camera…');
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
			if(stopped) { for(const t of stream.getTracks()) t.stop(); }
			else { ownedStream = stream; showStream(stream); }
		} catch(e) {
			if(!stopped) showMessage('Camera unavailable');
		}
	}

	host.onDestroy(() => {
		stopped = true;
		unsubscribeOwn?.();
		stopWatching?.();
		unsubscribeCallers?.();
		overlay.destroy();
		if(ownedStream) for(const t of ownedStream.getTracks()) t.stop();
		video.remove();
		placeholder.remove();
	});
}
